import {t} from 'i18next';
import type {CustomMenuItem} from '../components/common/arr-menu/ArrMenu';
import type {SelectSubItem} from '../components/doc-explorer/doc-list/table-header/TableHeader';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_INTERNAL_DOMAINS,
  GA_PREFIX,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../constants/analyticsConstants';
import {
  GA_PAGE_MAPPINGS,
  GAPageMapping,
} from '../constants/analyticsPageMappings';
import {
  GADataLayerEvent,
  GAEvent,
  GAClickInfo,
  GAContextSelectedInfo,
  GAErrorInfo,
  GAPageInfo,
  GASubpageInfo,
  GAUserInfo,
  TrackGAEventParams,
} from '../types/analyticsTypes';

interface PushEventParams {
  eventName: GAEvent;
  page?: GAPageInfo;
  subPage?: GASubpageInfo;
  user?: GAUserInfo;
  click?: GAClickInfo;
  error?: GAErrorInfo;
  contextSelected?: GAContextSelectedInfo;
  fileExtension?: string;
}

function deepLowercase(obj: unknown): unknown {
  if (typeof obj === 'string') {
    return obj.toLowerCase();
  }

  if (Array.isArray(obj)) {
    return obj.map(deepLowercase);
  }

  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce(
      (acc, key) => {
        acc[key] = deepLowercase((obj as Record<string, unknown>)[key]);
        return acc;
      },
      {} as Record<string, unknown>
    );
  }

  return obj;
}

function applyPrePushFlush(event: GADataLayerEvent) {
  if (event.event === GA_EVENT_NAMES.PAGE_VIEW) {
    return [{click: undefined, error: undefined}];
  }

  return [];
}

function applyPostPushFlush(event: GADataLayerEvent) {
  const hasSubpage =
    event?.page && (event.page.subpage_name || event.page.subpage_type);

  if (hasSubpage) {
    return [
      {
        page: {
          subpage_name: undefined,
          subpage_type: undefined,
        },
      },
    ];
  }

  return [];
}

function stringifyWithUndefined(obj: unknown) {
  return JSON.stringify(
    obj,
    (_, value) => (value === undefined ? '__undefined__' : value),
    4
  );
}

function dataLayerPush(event: GADataLayerEvent, type: string) {
  window.dataLayer.push(event);
  // console.log(
  //   'Aurora_Review_GA',
  //   type,
  //   stringifyWithUndefined({
  //     ...event,
  //     user: event.user ? {...event.user, id: '###'} : undefined,
  //   })
  // );
}

function triggerDataLayerPush(event: GADataLayerEvent) {
  const normalizedEvent = deepLowercase(event) as GADataLayerEvent;

  applyPrePushFlush(normalizedEvent).forEach((flushEvent) =>
    dataLayerPush(flushEvent as GADataLayerEvent, 'pre_flush')
  );

  dataLayerPush(normalizedEvent, 'main');

  applyPostPushFlush(normalizedEvent).forEach((flushEvent) =>
    dataLayerPush(flushEvent as GADataLayerEvent, 'post_flush')
  );
}

export function pushToDataLayer(params: PushEventParams) {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  const dataLayerEvent: GADataLayerEvent = {
    event: params.eventName,
  };

  if (params.page) {
    dataLayerEvent.page = params.page;
  }
  if (params.user) {
    dataLayerEvent.user = params.user;
  }

  if (params.subPage) {
    dataLayerEvent.page = params.subPage;
  }
  if (params.click) {
    dataLayerEvent.click = params.click;
  }
  if (params.error) {
    dataLayerEvent.error = params.error;
  }
  if (params.contextSelected) {
    dataLayerEvent.context_selected = params.contextSelected;
  }
  if (params.fileExtension) {
    dataLayerEvent.file_extension = params.fileExtension;
  }

  triggerDataLayerPush(dataLayerEvent);
}

const SORTED_PAGE_MAPPINGS = [...GA_PAGE_MAPPINGS].sort(
  (a, b) => getPathSegments(b.url).length - getPathSegments(a.url).length
);

function normalizePathname(pathname: string): string {
  const normalized = pathname.toLowerCase().replace(/\/+$/, '');
  return normalized || '/';
}

function getPathSegments(path: string): string[] {
  return normalizePathname(path).replace(/^\/+/, '').split('/').filter(Boolean);
}

function isDynamicMappingSegment(segment: string): boolean {
  return /^\d+$/.test(segment) || segment.startsWith(':');
}

function mappingMatchesPath(mappingUrl: string, pathname: string): boolean {
  const mapSegs = getPathSegments(mappingUrl);
  const pathSegs = getPathSegments(pathname);

  if (mapSegs.length !== pathSegs.length) {
    return false;
  }

  return mapSegs.every(
    (segment, index) =>
      isDynamicMappingSegment(segment) || segment === pathSegs[index]
  );
}

function resolvePagenameFromPath(
  mapping: GAPageMapping,
  pathname: string
): string {
  const mapSegs = getPathSegments(mapping.url);
  const pathSegs = getPathSegments(pathname);
  let pagename = mapping.pagename;

  mapSegs.forEach((segment, index) => {
    const pathSegment = pathSegs[index];
    if (!pathSegment) {
      return;
    }

    if (/^\d+$/.test(segment)) {
      pagename = pagename.replace(`:${segment}`, `:${pathSegment}`);
      return;
    }

    if (segment.startsWith(':')) {
      const paramName = segment.slice(1);
      pagename = pagename.replace(`:${paramName}`, `:${pathSegment}`);
    }
  });

  return pagename;
}

function findPageMapping(pathname: string): GAPageMapping | undefined {
  const normalizedPath = normalizePathname(pathname);

  const exactMatch = SORTED_PAGE_MAPPINGS.find(
    (mapping) =>
      !getPathSegments(mapping.url).some(isDynamicMappingSegment) &&
      normalizePathname(mapping.url) === normalizedPath
  );

  if (exactMatch) {
    return exactMatch;
  }

  return SORTED_PAGE_MAPPINGS.find((mapping) =>
    mappingMatchesPath(mapping.url, normalizedPath)
  );
}

function generateFallbackPageAndSection(pathname: string): {
  page: string;
  section: string;
} {
  const segments = getPathSegments(pathname);

  return {
    page: `${GA_PREFIX}:${segments.join(':')}`,
    section: segments[0] || '',
  };
}

export const generatePageAndSection = (
  url: string
): {page: string; section: string} => {
  const parsed = new URL(url, window.location.origin);
  const mapping = findPageMapping(parsed.pathname);

  if (!mapping) {
    return generateFallbackPageAndSection(parsed.pathname);
  }

  return {
    page: resolvePagenameFromPath(mapping, parsed.pathname),
    section: mapping.section,
  };
};

export async function getGAHashForValue(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const trimmedText = value.toLowerCase().trim();
  const data = encoder.encode(trimmedText);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function buildChangeRoleContextSelected({
  roleName,
  domainId,
  projectId,
  securityGroupName,
}: {
  roleName?: string | number | null;
  domainId?: string | number | null;
  projectId?: string | number | null;
  securityGroupName?: string | number | null;
}): GAContextSelectedInfo {
  const contextSelected: GAContextSelectedInfo = {};

  if (roleName != null && String(roleName).trim()) {
    contextSelected.contextual_role = String(roleName);
  }

  if (domainId != null && String(domainId).trim()) {
    contextSelected.domain_id = String(domainId);
  }

  if (projectId != null && String(projectId).trim()) {
    contextSelected.project_id = String(projectId);
  }

  if (securityGroupName != null && String(securityGroupName).trim()) {
    contextSelected.security_group = String(securityGroupName);
  }

  return contextSelected;
}

function isCustomMenuItem(
  item: CustomMenuItem | SelectSubItem
): item is CustomMenuItem {
  return 'label' in item;
}

function getActionMenuItemName(item: CustomMenuItem | SelectSubItem): string {
  if (isCustomMenuItem(item)) {
    return typeof item.label === 'string' ? item.label.trim() : '';
  }

  return item.name.trim();
}

function hasNestedActionMenuItems(item: CustomMenuItem | SelectSubItem): boolean {
  if (isCustomMenuItem(item)) {
    return Boolean(item.submenu?.length);
  }

  return Boolean(item.subItems?.length);
}

export function trackActionMenuSelection(
  trackGAEvent: (params: TrackGAEventParams) => void,
  item?: CustomMenuItem | SelectSubItem | null
) {
  if (!item) return;
  if (hasNestedActionMenuItems(item)) return;

  const name = getActionMenuItemName(item);
  if (!name) return;

  trackGAEvent({
    eventName: GA_EVENT_NAMES.ACTION_MENU,
    click: {
      name,
      type: GA_CLICK_TYPES.MENU_ITEM,
      region: GA_REGIONS.MENU,
      action: t('gaTracking.global.actionSelected'),
    },
  });
}

export const SAVED_SEARCH_GROUP_MENU_ACTIONS: Record<string, string> = {
  add_group: 'Add to new group',
  batch_upload: 'Batch upload',
  rename: 'Rename',
  move: 'Move',
  delete: 'Delete',
  share: 'Share',
};

export function trackSavedSearchGroupMenuAction(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {
    actionId,
    categoryName,
  }: {
    actionId?: string;
    categoryName?: string;
  }
) {
  const name = actionId ? SAVED_SEARCH_GROUP_MENU_ACTIONS[actionId] : undefined;
  if (!name || !categoryName) return;

  trackGAEvent({
    eventName: GA_EVENT_NAMES.CLICK,
    click: {
      name,
      type: GA_CLICK_TYPES.DROPDOWN,
      region: GA_REGIONS.SAVED_SEARCH_GROUP_MENU,
      action: t('gaTracking.global.selectedSavedSearchGroupMenuAction'),
      details: t('gaTracking.global.savedSearchGroupMenuDetails', {
        name: categoryName,
      }),
    },
  });
}

export const SAVED_SEARCH_MENU_ACTIONS: Record<string, string> = {
  edit: 'Edit Search',
  rename: 'Rename',
  move: 'Move',
  share: 'Share',
  delete: 'Delete',
};

export function trackSavedSearchMenuAction(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {
    actionId,
    categoryName,
    searchId,
  }: {
    actionId?: string;
    categoryName?: string;
    searchId?: string | number;
  }
) {
  const name = actionId ? SAVED_SEARCH_MENU_ACTIONS[actionId] : undefined;
  if (!name || searchId == null || searchId === '') return;

  trackGAEvent({
    eventName: GA_EVENT_NAMES.CLICK,
    click: {
      name,
      type: GA_CLICK_TYPES.DROPDOWN,
      region: GA_REGIONS.SAVED_SEARCH_MENU,
      action: t('gaTracking.global.selectedSavedSearchMenuAction'),
      details: `${t('gaTracking.global.savedSearchGroupMenuDetails', {
        name: categoryName ?? '',
      })};${t('gaTracking.global.savedSearchIdDetails', {
        id: searchId,
      })}`,
    },
  });
}

export type GAModalViewConfig = {
  name?: string;
  details?: string;
};

export type GAModalClickConfig = {
  name?: string;
  action?: string;
  details?: string;
};

export function trackModalView(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {name, details}: GAModalViewConfig
) {
  const trimmedName = name?.trim();
  const trimmedDetails = details?.trim();
  if (!trimmedName) return;

  trackGAEvent({
    eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
    subPage: {
      subpage_name: trimmedName,
      subpage_type: GA_SUBPAGE_TYPE.MODAL,
      ...(trimmedDetails && {subpage_details: trimmedDetails}),
    },
  });
}

export function trackModalClick(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {
    name,
    action,
    details,
    subpageName,
  }: GAModalClickConfig & {subpageName?: string}
) {
  const trimmedName = name?.trim();
  const trimmedAction = action?.trim();
  const trimmedDetails = details?.trim();
  const trimmedSubpageName = subpageName?.trim();

  if (!trimmedName || !trimmedAction) return;

  trackGAEvent({
    eventName: GA_EVENT_NAMES.CLICK,
    click: {
      name: trimmedName,
      type: GA_CLICK_TYPES.BUTTON,
      region: GA_REGIONS.MODAL,
      action: trimmedAction,
      ...(trimmedDetails && {details: trimmedDetails}),
    },
    ...(trimmedSubpageName && {
      subPage: {
        subpage_name: trimmedSubpageName,
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    }),
  });
}

export function trackBatchUploadDownloadTemplateClick(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {
    categoryName,
    fileExtension = 'xlsx',
  }: {categoryName?: string; fileExtension?: string} = {}
) {
  trackGAEvent({
    eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
    ...(fileExtension && {fileExtension}),
    click: {
      name: t('gaTracking.global.downloadTemplateClickName'),
      type: GA_CLICK_TYPES.BUTTON,
      region: GA_REGIONS.MODAL,
      action: t('gaTracking.global.downloadedBatchUploadTemplate'),
      ...(categoryName && {
        details: t('gaTracking.global.savedSearchGroupMenuDetails', {
          name: categoryName,
        }),
      }),
    },
    subPage: {
      subpage_name: t('gaTracking.global.batchUploadSubpageName'),
      subpage_type: GA_SUBPAGE_TYPE.MODAL,
    },
  });
}

export function trackSavedSearchColumnAdded(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {columnName}: {columnName?: string}
) {
  trackSavedSearchColumnClick(trackGAEvent, {
    columnName,
    action: t('gaTracking.global.addedSavedSearchColumn'),
  });
}

export function trackSavedSearchColumnRemoved(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {columnName}: {columnName?: string}
) {
  trackSavedSearchColumnClick(trackGAEvent, {
    columnName,
    action: t('gaTracking.global.removedSavedSearchColumn'),
  });
}

function trackSavedSearchColumnClick(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {columnName, action}: {columnName?: string; action: string}
) {
  const name = columnName?.trim();
  if (!name) return;

  trackGAEvent({
    eventName: GA_EVENT_NAMES.CLICK,
    click: {
      name,
      type: GA_CLICK_TYPES.CHECKBOX,
      region: GA_REGIONS.SAVED_SEARCH_COLUMNS,
      action,
    },
  });
}

export function trackSavedSearchFilterApplied(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {
    hasStatus,
    hasSearchName,
    hasSearchId,
    statusValue,
  }: {
    hasStatus?: boolean;
    hasSearchName?: boolean;
    hasSearchId?: boolean;
    statusValue?: string;
  }
) {
  const categories = [
    ...(hasStatus ? [t('gaTracking.global.savedSearchFilterSelectStatus')] : []),
    ...(hasSearchName
      ? [t('gaTracking.global.savedSearchFilterSearchName')]
      : []),
    ...(hasSearchId ? [t('gaTracking.global.savedSearchFilterSearchId')] : []),
  ];
  if (categories.length === 0) return;

  trackGAEvent({
    eventName: GA_EVENT_NAMES.FILTER,
    click: {
      name: t('gaTracking.global.applyFilterClickName'),
      type: GA_CLICK_TYPES.BUTTON,
      region: GA_REGIONS.SAVED_SEARCH_FILTERS,
      action: t('gaTracking.global.filteredSavedSearchList'),
      details: t('gaTracking.global.savedSearchFilterCategoryDetails', {
        name: categories.join(','),
      }),
      ...(hasStatus &&
        statusValue && {
          more_details: t('gaTracking.global.savedSearchFilterStatusDetails', {
            name: statusValue,
          }),
        }),
    },
  });
}

export function trackSavedSearchResultsSorted(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {columnName}: {columnName?: string}
) {
  if (!columnName) return;

  trackGAEvent({
    eventName: GA_EVENT_NAMES.CLICK,
    click: {
      name: columnName,
      type: GA_CLICK_TYPES.LINK,
      region: GA_REGIONS.SAVED_SEARCH_RESULTS,
      action: t('gaTracking.global.sortedSavedSearchList'),
    },
  });
}

export type GAPaginationClickInfo =
  | {control: 'page'; page: number}
  | {control: 'arrow'; direction: 'left' | 'right'}
  | {control: 'perPage'; perPage: number};

export function trackPaginationClick(
  trackGAEvent: (params: TrackGAEventParams) => void,
  info: GAPaginationClickInfo
) {
  const name = getPaginationClickName(info);
  if (!name) return;

  trackGAEvent({
    eventName: GA_EVENT_NAMES.CLICK,
    click: {
      name,
      type: GA_CLICK_TYPES.LINK,
      region: GA_REGIONS.FOOTER,
      action: t('gaTracking.global.selectedPagination'),
    },
  });
}

function getPaginationClickName(
  info: GAPaginationClickInfo
): string | undefined {
  if (info.control === 'page') {
    if (!Number.isFinite(info.page) || info.page < 1) return;
    return String(info.page);
  }

  if (info.control === 'perPage') {
    if (!Number.isFinite(info.perPage) || info.perPage < 1) return;
    return t('gaTracking.global.paginationPerPageName', {
      perPage: info.perPage,
    });
  }

  if (info.direction === 'left') {
    return t('gaTracking.global.paginationLeftArrow');
  }

  if (info.direction === 'right') {
    return t('gaTracking.global.paginationRightArrow');
  }
}

function trackSavedSearchRun(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {
    categoryName,
    searchId,
    moreDetails,
  }: {
    categoryName?: string;
    searchId?: string | number;
    moreDetails: string;
  }
) {
  const details = t('gaTracking.global.savedSearchGroupMenuDetails', {
    name: categoryName ?? '',
  });

  trackGAEvent({
    eventName: GA_EVENT_NAMES.SEARCH,
    click: {
      name: t('gaTracking.global.runSearchClickName'),
      type: GA_CLICK_TYPES.BUTTON,
      region: GA_REGIONS.SAVED_SEARCH,
      action: t('gaTracking.global.ranSavedSearch'),
      details:
        searchId == null || searchId === ''
          ? details
          : `${details};${t('gaTracking.global.savedSearchIdDetails', {
              id: searchId,
            })}`,
      more_details: moreDetails,
    },
  });
}

export function trackSavedSearchRunIndividual(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {
    categoryName,
    searchId,
    resultCount,
  }: {
    categoryName?: string;
    searchId?: string | number;
    resultCount?: string | number;
  }
) {
  if (searchId == null || searchId === '') return;

  const hasResultCount =
    resultCount != null && String(resultCount).trim() !== '';

  trackSavedSearchRun(trackGAEvent, {
    categoryName,
    searchId,
    moreDetails: t('gaTracking.global.savedSearchIndividualRunDetails', {
      searchCount: hasResultCount
        ? String(resultCount)
        : t('gaTracking.global.savedSearchNoResultCount'),
    }),
  });
}

export function trackSavedSearchRunMultiple(
  trackGAEvent: (params: TrackGAEventParams) => void,
  {categoryName}: {categoryName?: string} = {}
) {
  trackSavedSearchRun(trackGAEvent, {
    categoryName,
    moreDetails: t('gaTracking.global.savedSearchMultipleRunDetails'),
  });
}

export function getAuroraReviewCentralClickAction(sourceAction?: string) {
  const source = sourceAction?.trim();
  return t('gaTracking.global.auroraReviewCentralClickAction', {
    source: source ? `${source} - ` : '',
  });
}

export function trackAuroraReviewCentralClick(
  trackGAEvent: (params: TrackGAEventParams) => void,
  options?: {sourceAction?: string; subPage?: GASubpageInfo}
) {
  trackGAEvent({
    eventName: GA_EVENT_NAMES.CLICK,
    click: {
      name: t('common.tooltipPopover.footerDesc'),
      type: GA_CLICK_TYPES.LINK,
      region: GA_REGIONS.MODAL,
      action: getAuroraReviewCentralClickAction(options?.sourceAction),
    },
    ...(options?.subPage && {subPage: options.subPage}),
  });
}

export type GAHelpAlertVariant = 'help' | 'alert';

export type GAHelpAlertSubpageType = 'modal' | 'tooltip';

export const GA_HELP_ALERT_VARIANT = {
  HELP: 'help',
  ALERT: 'alert',
} as const;

export type GAHelpAlertViewConfig = {
  variant?: GAHelpAlertVariant;
  subpageType: GAHelpAlertSubpageType;
  title?: string;
  details?: string;
};

export type GAHelpInfoClickConfig = {
  region?: string;
};

export function getHelpAlertSubPage({
  variant = GA_HELP_ALERT_VARIANT.HELP,
  subpageType,
  title,
  details,
}: GAHelpAlertViewConfig): GASubpageInfo | undefined {
  const trimmedTitle = title?.trim();
  const subpage_name =
    subpageType === GA_SUBPAGE_TYPE.MODAL
      ? trimmedTitle
        ? `${variant}:${trimmedTitle}`
        : undefined
      : variant;

  if (!subpage_name) return undefined;

  return {
    subpage_name,
    subpage_type: subpageType,
    ...(details && {subpage_details: details}),
  };
}

export function trackHelpOrAlertView(
  trackGAEvent: (params: TrackGAEventParams) => void,
  config: GAHelpAlertViewConfig
) {
  const subPage = getHelpAlertSubPage(config);
  if (!subPage) return;

  trackGAEvent({
    eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
    subPage,
  });
}

export function trackHelpInfoTooltipClicked(
  trackGAEvent: (params: TrackGAEventParams) => void,
  config: GAHelpAlertViewConfig,
  click: GAHelpInfoClickConfig = {}
) {
  const tooltipContext = config.details?.trim();
  if (!tooltipContext) return;

  const subPage = getHelpAlertSubPage(config);
  if (!subPage) return;

  trackGAEvent({
    eventName: GA_EVENT_NAMES.CLICK,
    click: {
      name: tooltipContext,
      type: config.subpageType,
      region: click.region ?? GA_REGIONS.MODAL,
      action: t('gaTracking.global.openedHelpInfoTooltip'),
    },
    subPage,
  });
}

export async function getGAUserDetailsFromEmail(email: string) {
  const trimmedText = email.toLowerCase().trim();
  const hashHex = await getGAHashForValue(trimmedText);

  const domain = trimmedText.split('@')[1];
  const category = GA_INTERNAL_DOMAINS.includes(domain?.toLowerCase())
    ? 'internal'
    : 'external';

  return {
    id: hashHex,
    company_id: domain,
    category,
  };
}

export function formatRoleNameForGA(roleName?: string | null): string {
  const normalizedRole = roleName?.trim().toLowerCase();
  return normalizedRole || 'no-role';
}

export const isCurrentUrlMatch = (
  pathname: string,
  list: string[]
): boolean => {
  return list.some((url) => {
    if (url === '/') {
      return pathname === '/';
    }
    return pathname === url || pathname.startsWith(`${url}/`);
  });
};

export function getGAClickActionFromHref(href: string): string {
  const url = new URL(href, window.location.origin);
  return `${url.host}${url.pathname}${url.search}`;
}
